import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../supabase/supabaseClient';
import styles from "../../Styles/licenseTimer.module.scss";

const LicenseTimer = () => {
  const { user, subscriptionActive, hasActiveLicense, hasActiveSubscription, refreshSubscriptionStatus } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);

  // Fetch license expiration time ONLY when hasActiveLicense is true
  useEffect(() => {
    const fetchLicenseExpiration = async () => {
      // Early return if not a license user
      if (!user || !hasActiveLicense || hasActiveSubscription) {
        setExpiresAt(null);
        setTimeRemaining(null);
        return;
      }

      try {
        const { data: licenseData } = await supabase
          .from('user_licenses')
          .select('expires_at')
          .eq('user_id', user.id)
          .gt('expires_at', new Date().toISOString())
          .order('expires_at', { ascending: false })
          .limit(1)
          .single();

        if (licenseData) {
          setExpiresAt(new Date(licenseData.expires_at));
        } else {
          setExpiresAt(null);
          setTimeRemaining(null);
        }
      } catch (error) {
        console.error('Error fetching license:', error);
        setExpiresAt(null);
        setTimeRemaining(null);
      }
    };

    fetchLicenseExpiration();
  }, [user, hasActiveLicense, hasActiveSubscription]);

  // Update countdown every second
  useEffect(() => {
    if (!expiresAt || !hasActiveLicense) {
      setTimeRemaining(null);
      return;
    }

    const updateTimer = () => {
      const now = new Date();
      const difference = expiresAt - now;

      if (difference <= 0) {
        setTimeRemaining(null);
        setExpiresAt(null);
        // Optionally refresh the page or trigger a re-auth check
        // window.location.reload();
        // nienke note 29/4/2026: you changed your authcontext file and you can now use refreshSubscriptionStatus();  instead of a hard page reload which is better for user experience
        refreshSubscriptionStatus(); 
        return;
      }

      const hours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeRemaining({ hours, minutes, seconds });
    };

    updateTimer(); // Initial update
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [expiresAt, hasActiveLicense]);

  // Don't show timer if not a 24-hour license user
  if (!user || !hasActiveLicense || hasActiveSubscription || !timeRemaining) return null;

  return (
    <div className={styles.licenseTimer}>
      <div className={styles.timerContent}>
        <svg className={styles.timerIcon} width={18} height={18} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 6a.75.75 0 0 0-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 0 0 0-1.5h-3.75V6Z" clipRule="evenodd" />
        </svg>

        <div className={styles.timerAndLicenseText}>
          <span className={styles.licenseText}>
            24h Access</span>
          <span className={styles.timerText}>
            {String(timeRemaining.hours).padStart(2, '0')}:
            {String(timeRemaining.minutes).padStart(2, '0')}:
            {String(timeRemaining.seconds).padStart(2, '0')}

            {/* replace above with below for testing */}
            {/* {String(50).padStart(2, '0')} */}
          </span>
        </div>
      </div>
    </div>
  );
};

export default LicenseTimer;